import React, { useEffect, useState, useRef } from 'react';
import { CalendarIcon, MessageSquareIcon, DownloadIcon, Trash2Icon, CheckIcon, ChevronDownIcon, ChevronRightIcon, FileTextIcon, XIcon } from 'lucide-react';
import { Condition, ConditionStatus, STATUSES, STATUS_COLORS, ASSIGNEES, ASSIGNEE_COLORS, Document } from '../types/condition';
import { DocumentList } from './DocumentList';
type ConditionRowProps = {
  condition: Condition;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onUpdateStatus: (id: string, status: ConditionStatus) => void;
  onUpdateAssignee?: (id: string, assignee: string) => void;
  onUpdateTitle?: (id: string, title: string) => void;
  onUpdateDueDate?: (id: string, dueDate: string) => void;
  onDelete: (id: string) => void;
  onUploadDocument?: (conditionId: string, file: File) => void;
  onDeleteDocument?: (conditionId: string, documentId: string) => void;
  onPreviewDocument?: (document: Document) => void;
  onDownloadAllDocuments?: (conditionId: string) => void;
};
function getDueDateStatus(dueDate: string): {
  label: string;
  className: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return {
      label: `${Math.abs(diffDays)}d overdue`,
      className: 'text-red-600 font-medium'
    };
  } else if (diffDays === 0) {
    return {
      label: 'Due today',
      className: 'text-amber-600 font-medium'
    };
  } else if (diffDays <= 3) {
    return {
      label: `Due in ${diffDays}d`,
      className: 'text-amber-600'
    };
  } else {
    return {
      label: formatDate(dueDate),
      className: 'text-slate-600'
    };
  }
}
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function formatDateForInput(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
}
function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}
export function ConditionRow({
  condition,
  isSelected,
  onToggleSelect,
  onUpdateStatus,
  onUpdateAssignee,
  onUpdateTitle,
  onUpdateDueDate,
  onDelete,
  onUploadDocument,
  onDeleteDocument,
  onPreviewDocument,
  onDownloadAllDocuments
}: ConditionRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isAssigneeOpen, setIsAssigneeOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [editedTitle, setEditedTitle] = useState(condition.title);
  const [editedDueDate, setEditedDueDate] = useState(condition.dueDate);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  }>({
    left: 0,
    width: 0
  });
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  }>({
    left: 0,
    width: 0
  });
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const assigneeButtonRef = useRef<HTMLButtonElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const dueDateInputRef = useRef<HTMLInputElement>(null);
  const dueDateStatus = getDueDateStatus(condition.dueDate);
  const statusColors = STATUS_COLORS[condition.status];
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);
  useEffect(() => {
    if (isEditingDueDate && dueDateInputRef.current) {
      dueDateInputRef.current.focus();
    }
  }, [isEditingDueDate]);
  useEffect(() => {
    if (isStatusOpen && statusButtonRef.current) {
      const buttonRect = statusButtonRef.current.getBoundingClientRect();
      const dropdownHeight = 200;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setStatusDropdownPosition({
          bottom: window.innerHeight - buttonRect.top + 4,
          left: buttonRect.left,
          width: buttonRect.width
        });
      } else {
        setStatusDropdownPosition({
          top: buttonRect.bottom + 4,
          left: buttonRect.left,
          width: buttonRect.width
        });
      }
    }
  }, [isStatusOpen]);
  useEffect(() => {
    if (isAssigneeOpen && assigneeButtonRef.current) {
      const buttonRect = assigneeButtonRef.current.getBoundingClientRect();
      const dropdownHeight = 180;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setAssigneeDropdownPosition({
          bottom: window.innerHeight - buttonRect.top + 4,
          left: buttonRect.left,
          width: 160
        });
      } else {
        setAssigneeDropdownPosition({
          top: buttonRect.bottom + 4,
          left: buttonRect.left,
          width: 160
        });
      }
    }
  }, [isAssigneeOpen]);
  const handleStatusChange = (newStatus: ConditionStatus) => {
    onUpdateStatus(condition.id, newStatus);
    setIsStatusOpen(false);
  };
  const handleAssigneeChange = (newAssignee: string) => {
    if (onUpdateAssignee) {
      onUpdateAssignee(condition.id, newAssignee);
    }
    setIsAssigneeOpen(false);
  };
  const handleTitleSave = () => {
    if (editedTitle.trim() && editedTitle !== condition.title && onUpdateTitle) {
      onUpdateTitle(condition.id, editedTitle.trim());
    } else {
      setEditedTitle(condition.title);
    }
    setIsEditingTitle(false);
  };
  const handleTitleCancel = () => {
    setEditedTitle(condition.title);
    setIsEditingTitle(false);
  };
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTitleSave();else if (e.key === 'Escape') handleTitleCancel();
  };
  const handleDueDateSave = () => {
    if (editedDueDate && editedDueDate !== condition.dueDate && onUpdateDueDate) {
      onUpdateDueDate(condition.id, editedDueDate);
    } else {
      setEditedDueDate(condition.dueDate);
    }
    setIsEditingDueDate(false);
  };
  const handleDueDateCancel = () => {
    setEditedDueDate(condition.dueDate);
    setIsEditingDueDate(false);
  };
  const handleDueDateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleDueDateSave();else if (e.key === 'Escape') handleDueDateCancel();
  };
  const handleRowClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('a')) {
      return;
    }
    setIsExpanded(!isExpanded);
  };
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (assigneeButtonRef.current && !assigneeButtonRef.current.contains(event.target as Node)) {
        setIsAssigneeOpen(false);
      }
    }
    if (isAssigneeOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isAssigneeOpen]);
  const handleDownloadAll = () => {
    if (onDownloadAllDocuments) {
      onDownloadAllDocuments(condition.id);
    }
  };
  return <>
      <tr className={`
          transition-colors cursor-pointer
          ${isSelected ? 'bg-blue-50/70' : 'hover:bg-slate-50'}
          ${isExpanded ? '' : ''}
        `} onClick={handleRowClick} data-id="element-123">
        {/* Expand Icon */}
        <td className="px-4 py-3 w-12" data-id="element-124">
          <button onClick={e => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors" aria-label={isExpanded ? 'Collapse row' : 'Expand row'} data-id="element-125">
            {isExpanded ? <ChevronDownIcon className="w-4 h-4" data-id="element-126" /> : <ChevronRightIcon className="w-4 h-4" data-id="element-127" />}
          </button>
        </td>

        {/* Checkbox */}
        <td className="px-4 py-3 w-12" data-id="element-128">
          <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(condition.id)} onClick={e => e.stopPropagation()} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" aria-label={`Select ${condition.title}`} data-id="element-129" />
        </td>

        {/* Title */}
        <td className="px-4 py-3" data-id="element-130">
          <div className="flex items-center gap-2" data-id="element-131">
            {isEditingTitle ? <div className="flex items-center gap-2" onClick={e => e.stopPropagation()} data-id="element-132">
                <input ref={titleInputRef} type="text" value={editedTitle} onChange={e => setEditedTitle(e.target.value)} onKeyDown={handleTitleKeyDown} onBlur={handleTitleSave} className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Condition title" data-id="element-133" />
                <button onClick={handleTitleSave} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors" data-id="element-134">
                  <CheckIcon className="w-4 h-4" data-id="element-135" />
                </button>
                <button onClick={handleTitleCancel} className="p-1 text-slate-500 hover:bg-slate-100 rounded transition-colors" data-id="element-136">
                  <XIcon className="w-4 h-4" data-id="element-137" />
                </button>
              </div> : <>
                <button onClick={e => {
              e.stopPropagation();
              setIsEditingTitle(true);
            }} className="text-sm font-medium text-slate-900 hover:text-blue-600 hover:bg-slate-100 px-2 py-1 -mx-2 -my-1 rounded-md transition-colors text-left" data-id="element-138">
                  {condition.title}
                </button>
                {condition.priority === 'high' && <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold text-red-700 bg-red-100 rounded uppercase tracking-wide" data-id="element-139">
                    High
                  </span>}
                {condition.notes && <MessageSquareIcon className="w-4 h-4 text-slate-400 flex-shrink-0" title="Has notes" data-id="element-140" />}
              </>}
          </div>
        </td>

        {/* Status */}
        <td className="px-4 py-3 w-44" onClick={e => e.stopPropagation()} data-id="element-141">
          <div className="relative" data-id="element-142">
            <button ref={statusButtonRef} onClick={() => setIsStatusOpen(!isStatusOpen)} className={`
                inline-flex items-center justify-between gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all w-full
                ${statusColors.chip} ${statusColors.chipText}
                hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
              `} data-id="element-143">
              {condition.status}
              <ChevronDownIcon className="w-3.5 h-3.5 opacity-60" data-id="element-144" />
            </button>

            {isStatusOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setIsStatusOpen(false)} data-id="element-145" />
                <div className="fixed z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" style={{
              top: statusDropdownPosition.top,
              bottom: statusDropdownPosition.bottom,
              left: statusDropdownPosition.left,
              width: statusDropdownPosition.width
            }} data-id="element-146">
                  {STATUSES.map(status => {
                const colors = STATUS_COLORS[status];
                return <button key={status} onClick={() => handleStatusChange(status)} className={`
                          w-full px-3 py-2 text-sm text-left transition-colors
                          ${status === condition.status ? 'bg-blue-50 font-medium' : 'hover:bg-slate-50'}
                        `} data-id="element-147">
                        <span className={colors.text} data-id="element-148">{status}</span>
                      </button>;
              })}
                </div>
              </>}
          </div>
        </td>

        {/* Due Date */}
        <td className="px-4 py-3 w-36" data-id="element-149">
          {isEditingDueDate ? <div className="flex items-center gap-2" onClick={e => e.stopPropagation()} data-id="element-150">
              <input ref={dueDateInputRef} type="date" value={formatDateForInput(editedDueDate)} onChange={e => setEditedDueDate(e.target.value)} onKeyDown={handleDueDateKeyDown} onBlur={handleDueDateSave} className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-151" />
              <button onClick={handleDueDateSave} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors" data-id="element-152">
                <CheckIcon className="w-4 h-4" data-id="element-153" />
              </button>
              <button onClick={handleDueDateCancel} className="p-1 text-slate-500 hover:bg-slate-100 rounded transition-colors" data-id="element-154">
                <XIcon className="w-4 h-4" data-id="element-155" />
              </button>
            </div> : <button onClick={e => {
          e.stopPropagation();
          setIsEditingDueDate(true);
        }} className="inline-flex items-center gap-1.5 text-sm hover:bg-slate-100 px-2 py-1 -mx-2 -my-1 rounded-md transition-colors" data-id="element-156">
              <CalendarIcon className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-157" />
              <span className={dueDateStatus.className} data-id="element-158">
                {dueDateStatus.label}
              </span>
            </button>}
        </td>

        {/* Assignee */}
        <td className="px-4 py-3 w-32" onClick={e => e.stopPropagation()} data-id="element-159">
          <div className="relative" data-id="element-160">
            <button ref={assigneeButtonRef} onClick={() => setIsAssigneeOpen(!isAssigneeOpen)} className="inline-flex items-center gap-2 hover:bg-slate-100 rounded-md px-2 py-1 -mx-2 -my-1 transition-colors" data-id="element-161">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0 ${ASSIGNEE_COLORS[condition.assignee] || 'bg-slate-400'}`} data-id="element-162">
                {getInitials(condition.assignee)}
              </div>
              <span className="text-sm text-slate-700" data-id="element-163">
                {condition.assignee}
              </span>
            </button>

            {isAssigneeOpen && <>
                <div className="fixed inset-0 z-40" onClick={() => setIsAssigneeOpen(false)} data-id="element-164" />
                <div className="fixed z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" style={{
              top: assigneeDropdownPosition.top,
              bottom: assigneeDropdownPosition.bottom,
              left: assigneeDropdownPosition.left,
              width: assigneeDropdownPosition.width
            }} data-id="element-165">
                  {ASSIGNEES.map(assignee => <button key={assignee} onClick={() => handleAssigneeChange(assignee)} className={`
                        w-full px-3 py-2 text-sm text-left transition-colors flex items-center gap-2
                        ${condition.assignee === assignee ? 'bg-blue-50' : 'hover:bg-slate-50'}
                      `} data-id="element-166">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold text-white ${ASSIGNEE_COLORS[assignee] || 'bg-slate-400'}`} data-id="element-167">
                        {getInitials(assignee)}
                      </span>
                      <span className="flex-1" data-id="element-168">{assignee}</span>
                      {condition.assignee === assignee && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-169" />}
                    </button>)}
                </div>
              </>}
          </div>
        </td>

        {/* Documents Count */}
        <td className="px-4 py-3 w-28" data-id="element-170">
          <div className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-id="element-171">
            <FileTextIcon className="w-4 h-4" data-id="element-172" />
            <span className="tabular-nums" data-id="element-173">{condition.documents.length}</span>
          </div>
        </td>

        {/* Actions */}
        <td className="px-4 py-3 w-20" onClick={e => e.stopPropagation()} data-id="element-174">
          <div className="flex items-center gap-1" data-id="element-175">
            <button onClick={handleDownloadAll} disabled={condition.documents.length === 0} className={`
                p-1.5 rounded-md transition-colors
                ${condition.documents.length === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'}
              `} title={condition.documents.length === 0 ? 'No documents' : `Download ${condition.documents.length} doc${condition.documents.length === 1 ? '' : 's'}`} data-id="element-176">
              <DownloadIcon className="w-4 h-4" data-id="element-177" />
            </button>
            <button onClick={() => onDelete(condition.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete" data-id="element-178">
              <Trash2Icon className="w-4 h-4" data-id="element-179" />
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded Document Section */}
      {isExpanded && <tr className={isSelected ? 'bg-blue-50/70' : 'bg-slate-50/50'} data-id="element-180">
          <td colSpan={8} className="p-0" data-id="element-181">
            <DocumentList conditionId={condition.id} documents={condition.documents} onUpload={onUploadDocument || (() => {})} onDelete={onDeleteDocument || (() => {})} onPreview={onPreviewDocument || (() => {})} data-id="element-182" />
          </td>
        </tr>}
    </>;
}